import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";

const ORDER_TYPES = ["Market", "Limit", "Stop-Loss"];
const ORDER_SIDES = ["Buy", "Sell"];

const OrderPanel = ({ stock, onOrderSubmit }) => {
  const [orderType, setOrderType] = useState("Market");
  const [side, setSide] = useState("Buy");
  const [quantity, setQuantity] = useState("100");
  const [limitPrice, setLimitPrice] = useState(stock?.price?.toFixed(2));
  const [stopPrice, setStopPrice] = useState((stock?.price * 0.95)?.toFixed(2));
  const [useMargin, setUseMargin] = useState(false);
  const [leverage, setLeverage] = useState("2");
  const [submitted, setSubmitted] = useState(false);

  const qty = parseFloat(quantity) || 0;
  const price = orderType === "Market" ? stock?.price : parseFloat(limitPrice) || stock?.price;
  const total = qty * price;
  const marginRequired = useMargin ? total / parseFloat(leverage) : total;

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onOrderSubmit && onOrderSubmit({ side, orderType, quantity: qty, price, total, useMargin, leverage });
    }, 1500);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-5 sticky top-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Zap" size={16} color="var(--color-primary)" />
        <span className="font-heading text-sm font-semibold text-foreground">Place Order</span>
      </div>
      {/* Buy / Sell Toggle */}
      <div className="flex rounded-lg overflow-hidden border border-border mb-4">
        {ORDER_SIDES?.map(s => (
          <button key={s} onClick={() => setSide(s)}
            className="flex-1 py-2.5 text-sm font-heading font-semibold transition-all duration-150"
            style={side === s
              ? { background: s === "Buy" ? "var(--color-success)" : "var(--color-error)", color: "#fff" }
              : { background: "transparent", color: "var(--color-muted-foreground)" }}>
            {s}
          </button>
        ))}
      </div>
      {/* Order Type */}
      <div className="flex gap-1 mb-4">
        {ORDER_TYPES?.map(t => (
          <button key={t} onClick={() => setOrderType(t)}
            className={`flex-1 py-1.5 text-xs font-caption rounded-md border transition-all duration-150 ${orderType === t ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/30"}`}
            style={orderType === t ? { color: "var(--color-primary)", borderColor: "rgba(0,217,255,0.4)", background: "rgba(0,217,255,0.08)" } : {}}>
            {t}
          </button>
        ))}
      </div>
      {/* Current Price */}
      <div className="flex items-center justify-between mb-3 p-2.5 rounded-lg bg-secondary/60 border border-border/50">
        <span className="text-xs text-muted-foreground font-caption">Current Price</span>
        <span className="font-heading text-sm font-bold" style={{ color: "var(--color-primary)" }}>${stock?.price?.toFixed(2)}</span>
      </div>
      {/* Quantity */}
      <div className="mb-3">
        <Input label="Quantity (Shares)" type="number" value={quantity}
          onChange={e => setQuantity(e?.target?.value)} placeholder="100" min="1" />
      </div>
      {/* Limit Price */}
      {orderType === "Limit" && (
        <div className="mb-3">
          <Input label="Limit Price ($)" type="number" value={limitPrice}
            onChange={e => setLimitPrice(e?.target?.value)} placeholder={stock?.price?.toFixed(2)} />
        </div>
      )}
      {/* Stop Price */}
      {orderType === "Stop-Loss" && (
        <div className="mb-3">
          <Input label="Stop Price ($)" type="number" value={stopPrice}
            onChange={e => setStopPrice(e?.target?.value)} placeholder={(stock?.price * 0.95)?.toFixed(2)} />
        </div>
      )}
      {/* Margin Toggle */}
      <div className="flex items-center justify-between mb-3 p-2.5 rounded-lg bg-secondary/60 border border-border/50">
        <div className="flex items-center gap-2">
          <Icon name="Layers" size={14} color="var(--color-accent)" />
          <span className="text-xs text-foreground font-caption">Margin Trading</span>
        </div>
        <button onClick={() => setUseMargin(v => !v)}
          className={`w-10 h-5 rounded-full transition-all duration-200 relative ${useMargin ? "bg-accent" : "bg-muted"}`}
          style={{ background: useMargin ? "var(--color-accent)" : "var(--color-muted)" }}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${useMargin ? "left-5" : "left-0.5"}`} />
        </button>
      </div>
      {useMargin && (
        <div className="mb-3">
          <Input label="Leverage (x)" type="number" value={leverage}
            onChange={e => setLeverage(e?.target?.value)} placeholder="2" min="1" max="10" />
        </div>
      )}
      {/* Order Summary */}
      <div className="bg-secondary/60 rounded-lg p-3 border border-border/50 mb-4 space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground font-caption">Est. Total</span>
          <span className="font-mono font-semibold text-foreground">${total?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        {useMargin && (
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground font-caption">Margin Required</span>
            <span className="font-mono font-semibold" style={{ color: "var(--color-accent)" }}>${marginRequired?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground font-caption">Order Type</span>
          <span className="font-caption text-foreground">{orderType}</span>
        </div>
      </div>
      <Button variant={side === "Buy" ? "success" : "danger"} fullWidth loading={submitted}
        onClick={handleSubmit} iconName={side === "Buy" ? "TrendingUp" : "TrendingDown"} iconPosition="left">
        {submitted ? "Submitting..." : `${side} ${qty > 0 ? qty?.toLocaleString() : ""} ${stock?.symbol}`}
      </Button>
      {submitted && (
        <div className="mt-2 text-xs text-center font-caption" style={{ color: "var(--color-success)" }}>
          ✓ Order submitted to exchange
        </div>
      )}
    </div>
  );
};

export default OrderPanel;